import { put, call, select, takeLatest } from "redux-saga/effects";
import { fetchOrgAccessUsersIds, provideOrgAccess, removeOrgAccess, searchOrgUsers } from "./orgAccessApi";
import { fetchOrgUsersListError, fetchOrgUsersListRequest, fetchOrgUsersListSuccess,
  searchOrgUsersError, searchOrgUsersRequest, searchOrgUsersSuccess, provideOrgAccessError, provideOrgAccessRequest, provideOrgAccessSuccess, removeOrgUserAccessRequest, removeOrgUserAccessSuccess, removeOrgUserAccessError } from "./orgAccessSlice";
import { mapOrgAccessResponse, mapCommunitiesResponse, mapProvideOrgAccessRequest, mapRemoveOrgAccessRequest } from "./orgAccessMapper";
import { getOrgId } from "@/utils/authManager";
import { fetchUsersByIds } from "../users/usersAPI";
import { selectUserList } from "@/common/slice/userList";

const ADD_USER_PAGE_SIZE = 50;

export function* fetchOrgAccessListSaga() {
  try {
    const orgUsersIdsResponse = yield call(fetchOrgAccessUsersIds);
    const { communities } = mapCommunitiesResponse(orgUsersIdsResponse);

    // No community entries means nobody has access — don't fall through to an
    // unfiltered user list call.
    if (!communities.length) {
      yield put(fetchOrgUsersListSuccess({ orgUsersList: [] }));
      return;
    }

    const communityByUserId = new Map(
      communities.map((c) => [String(c.accessGivenTo), c.id]),
    );
    const userIds = Array.from(communityByUserId.keys());

    // Reuse users already in the userList store and fetch only the missing
    // ones. Read-only: the shared store is not written to from here.
    const userList = (yield select(selectUserList)) || {};
    const usersById = {};
    userIds.forEach((id) => {
      if (userList[id]?.id != null) usersById[id] = userList[id];
    });
    const missingIds = userIds.filter((id) => !usersById[id]);
    if (missingIds.length) {
      const response = yield call(fetchUsersByIds, missingIds);
      const fetched = (response && (response.users || response.Users)) || [];
      fetched.forEach((user) => {
        const id = user && (user.id ?? user.user_id);
        if (id != null) usersById[id] = user;
      });
    }
    const { users } = mapOrgAccessResponse({
      users: userIds.map((id) => usersById[id]).filter(Boolean),
    });

    // Attach community.id to each user; it's what the remove call needs.
    const orgUsersList = users.map((user) => ({
      ...user,
      communityId: communityByUserId.get(String(user.id)),
    }));

    yield put(fetchOrgUsersListSuccess({ orgUsersList }));
  } catch (error) {
    yield put(
      fetchOrgUsersListError(
        error.message || "Failed to fetch org access list"
      )
    );
  }
}

export function* searchOrgUsersSaga(action) {
  try {
    const email = (action.payload?.email || "").trim();
    // Only search once an email is entered — never pull the unfiltered list.
    if (!email) {
      yield put(searchOrgUsersSuccess({ orgUserSearchedResult: [] }));
      return;
    }
    const params = [`page_number=1`, `page_size=${ADD_USER_PAGE_SIZE}`, `email=${encodeURIComponent(email)}`];

    const response = yield call(searchOrgUsers, { _query: params.join("&") });
    const { users } = mapOrgAccessResponse(response || {});

    // Only users from other orgs who don't already have access can be added.
    // Fall back to the session's org so a missing _co cookie can't turn this
    // into a "null" comparison that lets current-org users through.
    const sessionOrgId = yield select((state) => state.sessionContextOidc?.userView?.orgId);
    const currentOrgId = String(getOrgId() ?? sessionOrgId ?? "");
    const existingIds = new Set(
      (yield select((state) => state.adminPanel.orgUserData.orgUsersList)).map((u) => String(u.id)),
    );
    const needle = email.toLowerCase();
    const orgUserSearchedResult = users.filter(
      (user) =>
        String(user.orgId) !== currentOrgId &&
        !existingIds.has(String(user.id)) &&
        (!needle || user.email.toLowerCase().includes(needle)),
    );

    yield put(searchOrgUsersSuccess({ orgUserSearchedResult }));
  } catch (error) {
    yield put(searchOrgUsersError(error.message || "Failed to search users"));
  }
}

export function* provideOrgAccessSaga(action) {
  try {
    const { userId } = action.payload;
    const response = yield call(provideOrgAccess, mapProvideOrgAccessRequest(userId));
    yield put(provideOrgAccessSuccess({ provideOrgAccessResponse: response }));
    // Refresh the org access list after successfully providing access
    yield call(fetchOrgAccessListSaga);
  } catch (error) {
    yield put(provideOrgAccessError(error.message || "Failed to provide org access"));
  }
}

export function* removeOrgAccessSaga(action) {
  try {
    const { communityId, userId } = action.payload;
    yield call(removeOrgAccess, mapRemoveOrgAccessRequest(communityId, userId));
    yield put(removeOrgUserAccessSuccess({ communityId }));
  } catch (error) {
    yield put(removeOrgUserAccessError(error.message || "Failed to remove org access"));
  }
}

export default function* orgAccessSaga() {
  yield takeLatest(fetchOrgUsersListRequest.type, fetchOrgAccessListSaga);
  yield takeLatest(searchOrgUsersRequest.type, searchOrgUsersSaga);
  yield takeLatest(provideOrgAccessRequest.type, provideOrgAccessSaga);
  yield takeLatest(removeOrgUserAccessRequest.type, removeOrgAccessSaga);
}
